/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  MODULE_LAYOUTS,
  ModuleLayout,
  ShowMoreGridSection,
  getModuleLayoutConfig,
  isHorizontalLayout,
  isInvertedColor,
} from '@coursera-web/cds-labs';
import type { GridConfig, ModuleLayoutConfig } from '@coursera-web/cds-labs';
import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import {
  Button,
  Chip,
  ChipGroup,
  Hidden,
  SelectOption,
  Typography2,
  breakpoints,
  typography2,
  useMediaQuery,
} from '@coursera/cds-core';
import type { Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import ProductCardCollectionPlaceholder from 'bundles/collections-common/components/ProductCardCollectionPlaceholder';
import RoundedSelectField from 'bundles/unified-common/components/RoundedSelectField';
import type { CareerRoleType, CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';

import _t from 'i18n!nls/unified-common';

export type CTAVariant = 'primary' | 'secondary' | 'ghost';

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: CTAVariant | null;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type DropdownMenu = {
  defaultValue?: string;
  label?: string;
  options?: {
    value: string;
    label: string;
  }[];
};

export type Filter = {
  id: string;
  filterName: string;
};

type UpsellCard = {
  id: string;
  position: number;
  renderComponent: (props: { index: number }) => React.ReactElement;
};

export type Props = {
  headline?: string | null;
  description?: string | null;
  showAllEntries?: boolean | null;
  filters?: Filter[] | null;
  collection?: CareerRolesCollectionType | null;
  onFilterChange?: (index: number) => void;
  isCollectionLoading?: boolean;
  currentFilterIndex?: number;
  numOfEntriesPerCollection?: number;
  defaultRows?: number;
  defaultMobileRows?: number;
  cta?: CTA;
  dropdownMenu?: DropdownMenu;
  currentDropdownValue?: PathwayLevel;
  onDropdownMenuChange?: (value: PathwayLevel) => void;
  ProductCardComponent: React.ElementType;
  numOfEntriesPerRow?: number;
  onExpandButtonClick?: <T>(value: T) => void;
  showPathwaysLoadingState?: boolean;
  upsellCards?: UpsellCard[];
  overrideItemsGridConfig?: GridConfig;
  overrideContentOffsets?: SerializedStyles;
} & ModuleLayoutConfig;

const styles = {
  description: css`
    margin-top: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-50) 0 var(--cds-spacing-100) 0;
    }
  `,
  headline: css`
    /* For Pathways MVP; cleaner wrapping/line break (Choose / your role) */
    ${breakpoints.up('md')} {
      padding-right: var(--cds-spacing-300);
    }
  `,
  filters: css`
    padding: var(--cds-spacing-50);

    && {
      width: max-content;
      display: flex;
      flex-wrap: nowrap;
      gap: var(--cds-spacing-150);
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        display: none;
      }

      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `,
  dropdownMenu: css`
    margin-left: var(--cds-spacing-200);

    ${breakpoints.up('md')} {
      margin-left: 0;
    }
  `,
  controls: css`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    align-items: center;
    margin-bottom: var(--cds-spacing-300);
    margin-left: calc(var(--cds-spacing-200) * -1);
    margin-right: calc(var(--cds-spacing-200) * -1);

    &::-webkit-scrollbar {
      display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;

    ${breakpoints.up('md')} {
      margin-left: 0;
      margin-right: calc(var(--cds-spacing-600) * -1);
    }
  `,
  selectOption: css`
    font: ${typography2.actionSecondary.font} !important;
    letter-spacing: ${typography2.actionSecondary.letterSpacing} !important;
    margin: var(--cds-spacing-100);

    .cds-selectOption-container {
      ${typography2.actionSecondary};
      :hover,
      :active {
        color: var(--cds-color-interactive-primary-hover);
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
      }
    }
  `,
  content: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: start;
  `,
  contentOffsets: css`
    ${breakpoints.up('lg')} {
      margin-top: calc(var(--cds-spacing-1400) + var(--cds-spacing-600));
    }
  `,
  contentWithInlineButton: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `,
  verticalLine: css`
    display: flex;
    border-right: 1px solid var(--cds-color-neutral-disabled-strong);
    height: 32px;
    margin-left: var(--cds-spacing-200);
    margin-right: var(--cds-spacing-200);
    overflow: hidden;
  `,
  alignCta: css`
    margin-top: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
  ghostAlignment: css`
    margin-left: calc(var(--cds-spacing-200) * -1);
  `,
  collectionsWrapper: css`
    width: 100%;
  `,
  collectionWrapperVertical: css`
    margin-top: calc(var(--cds-spacing-400) * -1);

    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,
  collection: css`
    margin-top: calc(var(--cds-spacing-400) * -1);
    margin-bottom: calc(var(--cds-spacing-200) * -1);

    ${breakpoints.up('xs')} {
      .rc-ProductCardCollectionPlaceholder {
        .cds-ProductCard-grid {
          min-height: 500px;
        }
      }
    }
  `,
  showMoreGridSection: css`
    && {
      padding-top: var(--cds-spacing-300);
      padding-bottom: var(--cds-spacing-200);
    }
  `,
  button: css`
    margin-top: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }

    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,
  noResultsFound: css`
    height: 250px;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.up('md')} {
      height: 500px;
    }

    p {
      margin-top: var(--cds-spacing-100);
    }
  `,
  placeholders: css`
    margin-bottom: calc(var(--cds-spacing-600) * -1);
  `,
  upsellContainer: css`
    max-width: 450px;
    height: 100%;

    ${breakpoints.up('sm')} {
      min-width: 230px;
      min-height: 400px;
    }

    ${breakpoints.down('xs')} {
      max-width: none;
    }
  `,
};

/**
 * Inserts the first upsell into the collection of roles.
 * Can be updated in the future to handle multiple upsells if needed,
 * but currently that is not something we need to worry about handling.
 */
const insertFirstUpsellIntoCollection = (
  roles: CareerRoleType[],
  upsells: UpsellCard[]
): Array<CareerRoleType | UpsellCard> => {
  if (!upsells.length) return roles;
  const final: Array<CareerRoleType | UpsellCard> = [...roles];
  const firstUpsell = upsells[0];
  // splice automatically handles inserting in the final position if the starting array is too small
  // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  final.splice(firstUpsell.position, 0, firstUpsell);
  return final;
};

function getItemGridConfig(isHorizontal: boolean): GridConfig {
  return isHorizontal ? { lg: 4, md: 4, sm: 6, xs: 12 } : { lg: 3, md: 3, sm: 6, xs: 12 };
}

/**
 * @description The CareerAcademyModule is a module that displays a collection of career roles.
 */
function CareerAcademyModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    backgroundColor,
    cta,
    description,
    headline,
    collection,
    isCollectionLoading,
    onFilterChange,
    currentFilterIndex = 0,
    numOfEntriesPerCollection = 3,
    defaultRows = 1,
    defaultMobileRows = 3,
    dropdownMenu,
    currentDropdownValue,
    onDropdownMenuChange,
    ProductCardComponent,
    onExpandButtonClick,
    showPathwaysLoadingState,
    upsellCards,
    overrideItemsGridConfig,
    overrideContentOffsets = styles.contentOffsets,
  } = props;
  const track = useTracker();
  const layoutConfig = getModuleLayoutConfig(props);
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const isTwoByTenLayout = layout === MODULE_LAYOUTS.text2image10 || layout === MODULE_LAYOUTS.image10text2;

  const isHorizontal = isHorizontalLayout({ layout });
  const itemsGridConfig: GridConfig = overrideItemsGridConfig || getItemGridConfig(isHorizontal);

  const numOfRolesToDisplay =
    showAllEntries && numOfEntriesPerCollection && numOfEntriesPerCollection % 2 !== 0 && isTabletOrSmaller && !isMobile
      ? numOfEntriesPerCollection - 1
      : numOfEntriesPerCollection;

  const isInverted = isInvertedColor(backgroundColor ?? '');
  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  const rolesToDisplay = collection?.entities?.slice(0, numOfRolesToDisplay) ?? [];

  let rowsToDisplay = defaultRows;
  if (isTabletOrSmaller) rowsToDisplay = Math.floor(defaultMobileRows / 2);
  if (isMobile) rowsToDisplay = defaultMobileRows;

  const filteredRoles =
    currentDropdownValue !== undefined
      ? rolesToDisplay?.map((role) => ({
          ...role,
          ...(role?.pathways && {
            pathways: {
              ...role.pathways,
              entities: role.pathways?.entities?.filter((pathway) => {
                return pathway?.proficiencyLevel && pathway.proficiencyLevel === currentDropdownValue;
              }),
            },
          }),
        }))
      : rolesToDisplay;

  const filteredRolesPlusUpsells: Array<CareerRoleType | UpsellCard> = insertFirstUpsellIntoCollection(
    filteredRoles,
    upsellCards ?? []
  );

  const button =
    cta?.text && cta?.target ? (
      <div
        css={[
          layout === MODULE_LAYOUTS.textTopImageBottom ? undefined : styles.alignCta,
          cta.variant === 'ghost' ? styles.ghostAlignment : undefined,
        ]}
      >
        <Button
          component="a"
          variant={variantOfCta}
          icon={cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
          href={cta.target}
          onClick={cta.onClick}
          css={styles.button}
          data-testid="career-academy-module-cta"
        >
          {cta.text}
        </Button>
      </div>
    ) : null;

  const handleDropdownChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const filterName = event.target.value as PathwayLevel;
    track('click_button', {
      button: { name: 'collections_filter_chip', linkName: `collection_filter_chip_${filterName.toLowerCase()}` },
    });

    onDropdownMenuChange?.(filterName);
  };

  const handleFilterChange = (index: number) => () => {
    const selectedFilter = filters?.[index]?.id.toLowerCase();
    track('click_button', {
      button: { name: 'collections_filter_chip', linkName: `collection_filter_chip_${selectedFilter}` },
    });

    onFilterChange?.(index);
  };

  const renderImage = () => (
    <div css={[styles.collectionsWrapper, isHorizontal ? undefined : styles.collectionWrapperVertical]}>
      <div css={styles.controls}>
        {dropdownMenu && dropdownMenu.options && (
          <React.Fragment>
            <RoundedSelectField
              value={currentDropdownValue}
              onChange={handleDropdownChange}
              aria-label={dropdownMenu.label || ''}
              css={styles.dropdownMenu}
            >
              {dropdownMenu.options.map((option) => (
                <SelectOption key={option.value} value={option.value} css={styles.selectOption}>
                  {option.label}
                </SelectOption>
              ))}
            </RoundedSelectField>
            <div css={styles.verticalLine} />
          </React.Fragment>
        )}
        {filters && filters.length > 1 && (
          <ChipGroup aria-label={_t('Filter By')} css={styles.filters}>
            {filters.map(({ filterName, id }, idx) => (
              <Chip
                key={id}
                selected={currentFilterIndex === idx}
                onClick={handleFilterChange(idx)}
                data-testid={`filter-${idx}`}
              >
                {filterName}
              </Chip>
            ))}
          </ChipGroup>
        )}
      </div>
      <div css={styles.collection} data-testid="product-cards-collection">
        {isCollectionLoading && (
          <React.Fragment>
            <Hidden smUp css={styles.placeholders}>
              <ProductCardCollectionPlaceholder
                data-testid="placeholder-mobile"
                itemsGridConfig={itemsGridConfig}
                hideTitle={true}
                hideButton={true}
                defaultMobileRows={rowsToDisplay}
                shouldShowListView={true}
              />
            </Hidden>
            <Hidden only="xs" css={styles.placeholders}>
              <ProductCardCollectionPlaceholder
                data-testid="placeholder"
                itemsGridConfig={itemsGridConfig}
                hideTitle={true}
                hideButton={true}
                defaultRows={rowsToDisplay}
                shouldShowListView={false}
              />
            </Hidden>
          </React.Fragment>
        )}
        {!isCollectionLoading && filteredRoles && filteredRoles.length > 0 && (
          <ShowMoreGridSection<CareerRoleType | UpsellCard>
            items={filteredRolesPlusUpsells}
            renderItem={({ item, itemIndex }) => {
              if ('renderComponent' in item) {
                return <div css={styles.upsellContainer}>{item.renderComponent({ index: itemIndex })}</div>;
              }

              const currentFilterId = filters?.[currentFilterIndex]?.id;
              const eventingData = {
                ...((currentDropdownValue || currentFilterId) && {
                  filter: [
                    ...(currentDropdownValue ? [currentDropdownValue] : []),
                    ...(currentFilterId ? [currentFilterId] : []),
                  ],
                }),
                productCard: {
                  recommender: {
                    hash: collection?.recommenderConfigurationHash,
                    context: collection?.context,
                  },
                  index: itemIndex,
                },
                carousel: {
                  id: collection?.id,
                },
              };

              return (
                <ProductCardComponent
                  eventingData={eventingData}
                  {...item}
                  proficiencyLevel={currentDropdownValue}
                  showPathwaysLoadingState={showPathwaysLoadingState}
                />
              );
            }}
            itemsGridConfig={itemsGridConfig}
            spacing={{ xs: 12, sm: 24 }}
            defaultRows={rowsToDisplay}
            css={styles.showMoreGridSection}
            onExpandButtonClick={onExpandButtonClick}
          />
        )}
        {!isCollectionLoading && !filteredRoles.length && (
          <div css={styles.noResultsFound}>
            <Typography2 variant="subtitleLarge" component="h3">
              {_t('No results found for "#{proficiencyLevel} #{filterName}"', {
                proficiencyLevel: dropdownMenu?.options?.find((option) => option.value === currentDropdownValue)?.label,
                filterName: filters?.[currentFilterIndex]?.filterName,
              })}
            </Typography2>
            <Typography2 variant="bodyPrimary" component="p">
              {_t('Change your filters.')}
            </Typography2>
          </div>
        )}
      </div>
      <Hidden {...(isTwoByTenLayout ? { lgUp: true } : { mdUp: true })}>{button}</Hidden>
    </div>
  );

  const renderContent = () => (
    <div
      css={
        layout === MODULE_LAYOUTS.textTopImageBottom
          ? styles.contentWithInlineButton
          : [styles.content, overrideContentOffsets]
      }
    >
      <div>
        <Typography2 variant="titleSmall" component="h2" color="inherit" css={styles.headline}>
          {headline}
        </Typography2>
        {description && (
          <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}
      </div>
      <Hidden {...(isTwoByTenLayout ? { mdDown: true } : { smDown: true })}>{button}</Hidden>
    </div>
  );

  return <ModuleLayout renderContent={renderContent} renderImage={renderImage} assembly={layoutConfig} />;
}

export default CareerAcademyModule;
